import React, {useCallback, useRef} from 'react';
import {addEdge, Connection, Edge, Node, OnConnectStartParams, ReactFlowInstance} from 'reactflow';
import {flushSync} from 'react-dom';
import {RuleOperator, RuleResultType} from '../../../../../incase-domain/appliedRules';
import {ActionNodeData, RULE_TYPE_MODIFIER} from '../nodes.types';
import {ruleEdgeStyleForModifier} from '../../util/flow.styles';
import {ActionNodeCallbacks} from '../../util/rulesToFlowElements';

interface UseConnectionHandlersParams {
    nodes: Node[];
    setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
    setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
    makeCallbacks: (nodeId: string) => ActionNodeCallbacks;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useConnectionHandlers({nodes, setNodes, setEdges, makeCallbacks}: UseConnectionHandlersParams) {
    const rfInstance = useRef<ReactFlowInstance | null>(null);
    const connectingNodeId = useRef<string | null>(null);
    const connectingHandleId = useRef<string | null>(null);

    const isValidConnection = useCallback((connection: Connection): boolean => {
        const {sourceHandle, targetHandle, source, target} = connection;
        if (sourceHandle?.startsWith('answer-') && targetHandle === 'action-incoming') return true;
        if (
            sourceHandle === 'action-ghost-out' &&
            targetHandle === 'incoming' &&
            source?.startsWith('action-') &&
            target?.startsWith('slide-')
        )
            return true;
        return false;
    }, []);

    const onConnect = useCallback(
        (connection: Connection) => {
            if (connection.sourceHandle === 'action-ghost-out' && connection.target?.startsWith('slide-')) {
                const slideId = connection.target.replace('slide-', '');
                const nodeId = connection.source;
                setNodes((ns) =>
                    ns.map((n) =>
                        n.id === nodeId
                            ? {
                                  ...n,
                                  data: {
                                      ...n.data,
                                      slideIds: (n.data.slideIds as string[]).includes(slideId)
                                          ? n.data.slideIds
                                          : [...n.data.slideIds, slideId],
                                  },
                              }
                            : n
                    )
                );
                return;
            }

            const predEdgeId = `pred-${connection.target}-${connection.source}-${connection.sourceHandle}`;
            setEdges((es) => {
                const filtered = es.filter((e) => !(e.source === connection.source && e.sourceHandle === connection.sourceHandle));
                const actionNode = nodes.find((n) => n.id === connection.target);
                const modifier = RULE_TYPE_MODIFIER[(actionNode?.data as ActionNodeData)?.ruleType] ?? 'jump';
                return addEdge({...connection, id: predEdgeId, ...ruleEdgeStyleForModifier(modifier)}, filtered);
            });
        },
        [nodes, setNodes, setEdges]
    );

    const onConnectStart = useCallback((_: React.MouseEvent | React.TouchEvent, {nodeId, handleId}: OnConnectStartParams) => {
        connectingNodeId.current = nodeId;
        connectingHandleId.current = handleId;
    }, []);

    const onConnectEnd = useCallback(
        (event: MouseEvent | TouchEvent) => {
            const target = event.target as Element;
            const droppedOnPane = target.classList.contains('react-flow__pane');

            if (droppedOnPane && connectingHandleId.current?.startsWith('answer-') && rfInstance.current) {
                const {clientX, clientY} = 'touches' in event ? (event as TouchEvent).touches[0] : (event as MouseEvent);
                const position = rfInstance.current.screenToFlowPosition({x: clientX, y: clientY});
                const actionNodeId = `action-${Date.now()}`;

                const newNode: Node<ActionNodeData> = {
                    id: actionNodeId,
                    type: 'action',
                    position,
                    data: {
                        ruleType: RuleResultType.JUMP_TO_SLIDE,
                        slideIds: [],
                        operator: RuleOperator.IF,
                        ...makeCallbacks(actionNodeId),
                    },
                };

                const predEdgeId = `pred-${actionNodeId}-${connectingNodeId.current}-${connectingHandleId.current}`;
                flushSync(() => {
                    setNodes((ns) => [...ns, newNode]);
                });
                setEdges((es) =>
                    addEdge(
                        {
                            id: predEdgeId,
                            source: connectingNodeId.current,
                            sourceHandle: connectingHandleId.current,
                            target: actionNodeId,
                            targetHandle: 'action-incoming',
                            ...ruleEdgeStyleForModifier('jump'),
                        },
                        es
                    )
                );
            }

            connectingNodeId.current = null;
            connectingHandleId.current = null;
        },
        [makeCallbacks, setNodes, setEdges]
    );

    return {rfInstance, isValidConnection, onConnect, onConnectStart, onConnectEnd};
}
